import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { usePortalFundInvestments } from '@/hooks/use-portal'
import { formatCurrency, formatDate } from '@/lib/format'

export function PortalInvestmentSection() {
  const { data, isLoading } = usePortalFundInvestments()

  const { totalInvestment, initialDate, funds } = useMemo(() => {
    const items = data?.data ?? []
    let total: number | null = null
    let earliest: string | null = null
    for (const fund of items) {
      if (fund.investedAmount != null) {
        total = (total ?? 0) + fund.investedAmount
      }
      if (
        fund.initialInvestmentDate &&
        (!earliest || fund.initialInvestmentDate < earliest)
      ) {
        earliest = fund.initialInvestmentDate
      }
    }
    return {
      totalInvestment: total,
      initialDate: earliest,
      funds: items,
    }
  }, [data])

  return (
    <div className="space-y-3.5 border-b border-primary/10 pb-3">
      <div className="flex flex-wrap items-baseline justify-between gap-1.5">
        <Typography variant="h6">Investment</Typography>
        <Typography variant="body-sm" as="span" className="text-primary/80">
          Initial Inv Date:{' '}
          {isLoading ? (
            <Skeleton className="inline-block h-3 w-20 align-middle" />
          ) : (
            formatDate(initialDate)
          )}
        </Typography>
      </div>

      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        <Card label="Fund">
          {isLoading ? (
            <Skeleton className="h-5 w-20" />
          ) : funds.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {[...funds]
                .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
                .map((fund) => (
                  <Tooltip key={fund.id}>
                    <TooltipTrigger>
                      <Badge className="rounded-sm">{fund.abbreviation}</Badge>
                    </TooltipTrigger>
                    <TooltipContent>{fund.displayName}</TooltipContent>
                  </Tooltip>
                ))}
            </div>
          ) : (
            <Typography variant="body" as="span" className="text-primary">
              —
            </Typography>
          )}
        </Card>

        <Card label="Total Invested">
          {isLoading ? (
            <Skeleton className="h-5 w-24" />
          ) : (
            <Typography
              variant="body"
              as="p"
              className="text-primary tabular-nums"
            >
              {formatCurrency(totalInvestment)}
            </Typography>
          )}
        </Card>
      </div>
    </div>
  )
}

interface CardProps {
  label: string
  children: ReactNode
}

function Card({ label, children }: CardProps) {
  return (
    <div className="flex flex-col justify-between rounded-md border border-primary/20 bg-white/50 pl-2 pr-1.5 pt-3 pb-2 dark:bg-gray-900">
      <Typography variant="body-sm" className="mb-1.5 text-primary/80">
        {label}
      </Typography>
      {children}
    </div>
  )
}
